import { Button, message } from "antd";
import ReactFlow, {
  Background,
  Connection,
  Controls,
  addEdge,
  useEdgesState,
  useNodesState,
  Node,
  MiniMap,
  NodeChange,
} from "reactflow";
import { nanoid } from "nanoid";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import MenuTemplateListDrawer from "../MenuTemplateListDrawer";
import { FlowProvider } from "./context";
import NodeAnswer from "../nodes/NodeAnswer";
import NodeGlobalVariable from "../nodes/NodeGlobalVariable";
import NodeQuestionInput from "../nodes/NodeQuestionInput";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import styles from "./index.less";
import "reactflow/dist/style.css";
import NodeSimple from "../nodes/NodeSimple";
import NodeTFJudge from "../nodes/NodeTFJudge";
import ButtonEdge from "../ButtonEdge";
import NodeContentExtract from "../nodes/NodeContentExtract";
import NodeHttp from "../nodes/NodeHttp";
import NodeClassifyQuestion from "../nodes/NodeClassifyQuestion";
import NodeTool from "../nodes/NodeTool";
import NodePluginInput from "../nodes/NodePluginInput";
import NodePluginOutput from "../nodes/NodePluginOutput";
import NodeMessageOutput from "../nodes/NodeMessageOutput";
import {
  FlowApp,
  getModuleListService,
  getSystemVariableService,
  getUsablePlugins,
} from "../../services/app";
import { getPluginListService } from "../../services/plugin";
import NodeGetAttributes from "../nodes/NodeGetAttributes";
import NodeSmartKnowledgeBase from "../nodes/NodeSmartKnowledgeBase";
import NodeTemplateParam from "../nodes/NodeTemplateParam";

import {
  FlowModuleItemType,
  ModuleInputKeyEnum,
  // ModuleInputKeyEnum,
  ModuleIOValueTypeEnum,
  ModuleItemType,
  ModuleOutputKeyEnum,
} from "@packages/flow-core/module";
import { appModule2FlowEdge, appModule2FlowNode } from "../../utils/adapt";
import { useRequest } from "ahooks";
import { ResponseCode } from "../../../constants";

import NodePullGroup from "../nodes/NodePullGroup";
import NodeJsonContentExtract from "../nodes/NodeJsonContentExtract";
import NodeRegexpContentExtract from "../nodes/NodeRegexpContentExtract";
import NodeKnowledgeBase from "../nodes/NodeKnowledgeBase";
import NodeCasesSwitcher from "../nodes/NodeCasesSwitcher";

import { generate_init_plugin_modules } from "./gen_plugin";

import NodeAdvancedKnowledgeBase from "../nodes/NodeAdvancedKnowledgeBase";
import { generate_init_template_modules } from "./gen_template";
import NodeCodeRunner from "../nodes/NodeCodeRunner";
import { usePageConfig } from "../../PageConfigProvider";
import { ReactComponent as MenuUnfoldIcon } from "../../../assets/menu-unfold-icon.svg";

const nodeTypes: Record<FlowNodeTypeEnum, any> = {
  [FlowNodeTypeEnum.userChatInput]: NodeQuestionInput,
  [FlowNodeTypeEnum.aiChat]: NodeSimple,
  [FlowNodeTypeEnum.knowledgeConcat]: undefined,
  [FlowNodeTypeEnum.knowledgeBase]: NodeKnowledgeBase,
  [FlowNodeTypeEnum.assignedAnswerNode]: NodeAnswer,
  [FlowNodeTypeEnum.classifyQuestion]: NodeClassifyQuestion,
  [FlowNodeTypeEnum.contentExtract]: NodeContentExtract,
  [FlowNodeTypeEnum.jsonContentExtract]: NodeJsonContentExtract,
  [FlowNodeTypeEnum.regexpContentExtract]: NodeRegexpContentExtract,
  [FlowNodeTypeEnum.httpRequest]: NodeHttp,
  [FlowNodeTypeEnum.pluginModule]: NodeSimple,
  [FlowNodeTypeEnum.pluginInput]: NodePluginInput,
  [FlowNodeTypeEnum.pluginOutput]: NodePluginOutput,
  [FlowNodeTypeEnum.queryExtension]: NodeSimple,
  [FlowNodeTypeEnum.globalVariable]: NodeGlobalVariable,
  [FlowNodeTypeEnum.delay]: NodeSimple,
  [FlowNodeTypeEnum.contentProcess]: NodeSimple,
  [FlowNodeTypeEnum.tools]: NodeTool,
  [FlowNodeTypeEnum.tfJudge]: NodeTFJudge,
  [FlowNodeTypeEnum.chatHistory]: NodeSimple,
  [FlowNodeTypeEnum.messageOutput]: NodeMessageOutput,
  [FlowNodeTypeEnum.setChatAttributes]: NodeSimple,
  [FlowNodeTypeEnum.getChatAttributes]: NodeGetAttributes,
  [FlowNodeTypeEnum.setWxWorkTag]: NodeSimple,
  [FlowNodeTypeEnum.getWxWorkTag]: NodeSimple,
  [FlowNodeTypeEnum.setWxWorkRoomName]: NodeSimple,
  [FlowNodeTypeEnum.setWxWorkRoomNotice]: NodeSimple,
  [FlowNodeTypeEnum.runApp]: undefined,
  [FlowNodeTypeEnum.cardOutput]: NodeSimple,
  [FlowNodeTypeEnum.videoOutput]: NodeSimple,
  [FlowNodeTypeEnum.imageOutput]: NodeSimple,
  [FlowNodeTypeEnum.fileOutput]: NodeSimple,
  [FlowNodeTypeEnum.voiceOutput]: NodeSimple, // 语音输出
  [FlowNodeTypeEnum.markdownOutput]: NodeMessageOutput, // markdown 输出
  [FlowNodeTypeEnum.mailOutput]: NodeSimple, // 邮件输出
  [FlowNodeTypeEnum.cancelFlow]: NodeSimple,
  [FlowNodeTypeEnum.pullGroup]: NodePullGroup,
  [FlowNodeTypeEnum.casesSwitcher]: NodeCasesSwitcher, // 新的判断器类型
  [FlowNodeTypeEnum.advancedKnowledgeBase]: NodeAdvancedKnowledgeBase,
  [FlowNodeTypeEnum.smartKnowledgeBase]: NodeSmartKnowledgeBase,
  [FlowNodeTypeEnum.invokeApp]: NodeSimple,
  [FlowNodeTypeEnum.templateParam]: NodeTemplateParam,
  [FlowNodeTypeEnum.codeRunner]: NodeCodeRunner, // 脚本运行
  [FlowNodeTypeEnum.rpaRequest]: NodeHttp, // rpa 节点
  // 12Times标签
  [FlowNodeTypeEnum.setDamselfishTag]: NodeSimple,
  [FlowNodeTypeEnum.getDamselfishTag]: NodeSimple,
  [FlowNodeTypeEnum.deleteDamselfishTag]: NodeSimple,
  // 12Times群标签
  [FlowNodeTypeEnum.setDamselfishRoomTag]: NodeSimple,
  [FlowNodeTypeEnum.getDamselfishRoomTag]: NodeSimple,
  [FlowNodeTypeEnum.deleteDamselfishRoomTag]: NodeSimple,
  // XF-1247 新增知识库产品类型
  [FlowNodeTypeEnum.normalQuestionsKnowledgeBase]: NodeSimple, // 常见问题知识库
  [FlowNodeTypeEnum.correctionKnowledgeBase]: NodeSimple, // 纠偏知识库
  [FlowNodeTypeEnum.knowledgeCenterSearch]: NodeSimple, // 知识中心搜索
  [FlowNodeTypeEnum.escapeText]: NodeSimple, // 转义文本
  [FlowNodeTypeEnum.miniProgramOutput]: NodeSimple, // 小程序输出
};

const edgeTypes = {
  default: ButtonEdge,
};

export type FlowEditProps = {
  defaultValue?: FlowApp;
  initialNodes?: Node<FlowModuleItemType>[];
  header?: ReactNode | ReactNode[];
  mode: "app" | "plugin" | "template";
};
export default function FlowEdit(props: FlowEditProps) {
  const { defaultValue } = usePageConfig();

  const [nodes, setNodes, onNodesChange] = useNodesState<FlowModuleItemType>(
    props.initialNodes ?? []
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;

  const getAllNodes = useCallback(() => {
    return nodesRef.current;
  }, []);

  const getAllEdges = useCallback(() => {
    return edgesRef.current;
  }, []);

  const [modalVisible, setModalVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // const { data: llmModels } = useRequest(async () => {
  //   const rsp = await getLLMModelsSerivce();
  //   return rsp.data;
  // });

  // 获取现有可用子流程模板
  const { data: useablePlugins } = useRequest(async () => {
    const rsp = await getPluginListService({ size: 999999999 });
    if (rsp.code === ResponseCode.Success) {
      const result = getUsablePlugins(rsp.data.records ?? []);
      // 过滤同一个子流程
      return props.mode === "plugin"
        ? result.filter(
            (r) =>
              r.inputs.findIndex(
                (input) =>
                  input.key === ModuleInputKeyEnum.selectPlugin &&
                  input.value === defaultValue?.id
              ) < 0
          )
        : result;
    }

    return [];
  });

  const onConnect = (connect: Connection) => {
    const source = nodes.find((node) => node.id === connect.source)?.data;
    const sourceType = (() => {
      const type = source?.outputs.find(
        (output) => output.key === connect.sourceHandle
      )?.valueType;

      if (source?.flowType === FlowNodeTypeEnum.classifyQuestion && !type) {
        return ModuleIOValueTypeEnum.boolean;
      }
      if (source?.flowType === FlowNodeTypeEnum.tools) {
        return ModuleIOValueTypeEnum.tools;
      }
      if (source?.flowType === FlowNodeTypeEnum.pluginInput) {
        return source?.inputs.find(
          (input) => input.key === connect.sourceHandle
        )?.valueType;
      }
      return source?.outputs.find(
        (output) => output.key === connect.sourceHandle
      )?.valueType;
    })();

    const targetType = nodes
      .find((node) => node.id === connect.target)
      ?.data?.inputs.find(
        (input) => input.key === connect.targetHandle
      )?.valueType;

    if (
      connect.sourceHandle === ModuleOutputKeyEnum.selectedTools &&
      connect.targetHandle === ModuleOutputKeyEnum.selectedTools
    ) {
    } else if (!sourceType || !targetType) {
      return message.warning("连接非法");
    } else if (
      targetType !== ModuleIOValueTypeEnum.any &&
      sourceType !== targetType
    ) {
      return message.warning("连接类型不一致");
    }
    setEdges((edges) => {
      return addEdge(
        {
          ...connect,
          type: "default",
        },
        edges
      );
    });
  };

  useEffect(() => {
    if (props.mode === "app" && props.defaultValue) {
      initData(props.defaultValue.modules || []);
      return;
    }

    if (props.mode === "plugin" && props.defaultValue) {
      const data = props.defaultValue.modules ?? [];
      if (data.length > 0) {
        initData(data);
        return;
      }
      // 为插件填充初始化数据
      initData(generate_init_plugin_modules(nanoid(), nanoid()));
    }

    if (props.mode === "template") {
      // 为模板填充初始化数据
      const data = props.defaultValue?.modules ?? [];
      if (data.length) {
        initData(data);
        return;
      }
      initData(generate_init_template_modules(nanoid()));
    }
  }, []);

  const initData = useCallback((modules: ModuleItemType[]) => {
    const edges = appModule2FlowEdge({
      modules,
    });
    setEdges(edges);

    setNodes(modules.map((item) => appModule2FlowNode({ item })));
  }, []);

  function handleConnect(connect: Connection) {
    if (!connect.sourceHandle || !connect.targetHandle) {
      return;
    }
    if (connect.source === connect.target) {
      return message.warning("不能自连接");
    }
    onConnect(connect);
  }

  const defaultViewport = useMemo(() => {
    return {
      zoom: 0.5,
      x: 0,
      y: 0,
      // y: (window.innerHeight >> 1) - 50,
      // x: (window.innerWidth >> 1) - 300,
    };
  }, []);

  const { data: templates } = useRequest(async () => {
    const rsp = await getModuleListService();
    if (rsp.code === ResponseCode.Success) {
      return rsp.data;
    } else {
      message.error(rsp.msg);
    }
  });

  const { data: systemVariables } = useRequest(async () => {
    const rsp = await getSystemVariableService();
    return (
      rsp.data?.map((item) => ({ label: item.title, key: item.value })) || []
    );
  });

  const handleNodesChange = (changes: NodeChange[]) => {
    for (const change of changes) {
      if (change.type === "remove") {
        const node = getAllNodes().find((n) => n.id === change.id);
        if (
          node?.data.flowType === FlowNodeTypeEnum.pluginInput ||
          node?.data.flowType === FlowNodeTypeEnum.pluginOutput
        ) {
          return message.error("不可删除输入、输出参数设置节点");
        }
        if (node?.data.flowType === FlowNodeTypeEnum.templateParam) {
          return message.error("不可删除模板变量设置节点");
        }
      }
    }
    onNodesChange(changes);
  };

  const providerValue = useMemo(() => {
    return {
      setEdges,
      setNodes,
      onEdgesChange,
      onNodesChange,
      getAllNodes,
      getAllEdges,
      mode: props.mode,
      templates,
      // llmModels,
      systemVariables,
      useablePlugins,
    };
  }, [templates, systemVariables, useablePlugins]);

  return (
    <FlowProvider value={providerValue}>
      {props.header}
      <div className={styles.container} ref={containerRef}>
        <ReactFlow
          nodeTypes={nodeTypes}
          nodes={nodes}
          onNodesChange={handleNodesChange}
          edges={edges}
          onEdgesChange={onEdgesChange}
          onConnect={handleConnect}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{
            animated: true,
            zIndex: 0,
            style: { strokeWidth: 2, stroke: "#5A646Es" },
          }}
          minZoom={0}
          elevateEdgesOnSelect
          defaultViewport={defaultViewport}
          fitView
        >
          <MiniMap zoomable pannable style={{ bottom: 40 }} />
          <Background style={{ background: "#F5F5F5" }} />
          <Controls position="bottom-right" style={{ display: "flex" }} />
          <Button
            style={{ display: modalVisible ? "none" : "block" }}
            className={styles["flow-btn"]}
            type="text"
            icon={<MenuUnfoldIcon />}
            onClick={() => {
              setModalVisible(!modalVisible);
            }}
          />
          <MenuTemplateListDrawer
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            containerGetBoundingClientRect={() =>
              containerRef.current?.getBoundingClientRect()
            }
          />
        </ReactFlow>
      </div>
    </FlowProvider>
  );
}
